import React, { Component } from 'react';
import { Avatar, Badge, Button, Icon, Menu, Modal } from 'antd';
import { inject, observer } from 'mobx-react';
import { LIST_LIMIT } from 'core/uiConstants';
import Router from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import GlobalModalContent from 'components/Global/Modal/ModalContent';
import Link from 'components/common/Link';
import checkIfSuperAdmin from 'core/utils/checkIfSuperAdmin';
import withNotificationAlert from 'graphql/hocs/encounter/withNotificationAlert';
import withNotifications from 'graphql/hocs/encounter/withNotifications';
import withMarkNotificationsAsRead from 'graphql/hocs/encounter/withMarkNotificationsAsRead';
import menus from 'registry/menus';
import DropdownWrapper from './DropdownWrapper';
import CdsDropdown from './CdsDropdown';
import CallsDropdown from './CallsDropdown';
import NotificationsDropdown from './NotificationsDropdown';

const Wrapper = styled.header`
  background-color: #fff;
  height: 80px;
  border-bottom: 1px solid rgba(72, 94, 144, 0.16);
  display: flex;

  .top-container {
    height: 100%;
    padding: 0 25px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;

    .top-container-2 {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .logo-container {
        display: flex;
        align-items: center;
      }

      .icons-container {
        display: flex;
      }
    }
  }

  .ant-menu-inline {
    border-right: none;
  }

  .menu-toggle {
    width: 80px;
    border-right: 1px solid rgba(72, 94, 144, 0.16);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .avatar-container {
    cursor: pointer;
  }
  .badgeWrapper {
    margin-right: 15px;
  }
`;

/* eslint-disable react/prefer-stateless-function */
@withNotificationAlert
@withNotifications
@withMarkNotificationsAsRead
@inject('authStore', 'uiStore')
@observer
class Topbar extends Component {
  hasAppMouseDownFired = false;

  hasProfileMouseDownFired = false;

  hasNotificationMouseDownFired = false;

  hasCallMouseDownFired = false;

  static propTypes = {
    authStore: PropTypes.object.isRequired,
    onGetNotifications: PropTypes.func.isRequired,
    notifAlert: PropTypes.object,
    onMarkNotificationsAsRead: PropTypes.func.isRequired,
    uiStore: PropTypes.object.isRequired,
  };

  static defaultProps = {
    notifAlert: null,
  };

  constructor(props) {
    super(props);
    this.appDropdown = React.createRef();
    this.callsListDropdown = React.createRef();
    this.cdsDropdown = React.createRef();
    this.notificationsListDropdown = React.createRef();
    this.profileDropdown = React.createRef();
  }

  state = {
    isAppDropdownVisible: false,
    isCdsVisible: false,
    isProfileDropdownVisible: false,
    isCallsListVisible: false,
    isNotificationsListVisible: false,
    notificationData: [],
  };

  async componentDidMount() {
    document.addEventListener('mousedown', this.handleMouseDown);
    const { onGetNotifications } = this.props;
    const { nodes } = await onGetNotifications({ limit: LIST_LIMIT });
    this.setState({ notificationData: nodes });
  }

  async componentDidUpdate(prevProps) {
    const { notifAlert: prevNotifEncounter } = prevProps;
    const { onGetNotifications, notifAlert } = this.props;

    if (prevNotifEncounter !== notifAlert) {
      const { nodes } = await onGetNotifications({ limit: LIST_LIMIT });
      this.setState({ notificationData: nodes });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleMouseDown);
  }

  handleClickAppMenu = e => {
    const { uiStore } = this.props;

    const clickedMenu = menus.find(m => m.key === e.key);

    if (clickedMenu.modal) {
      uiStore.openGlobalModal(e.modalKey);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (clickedMenu.refreshPage) {
        window.location.href = clickedMenu.to;
      } else {
        Router.push(clickedMenu.to, clickedMenu.to);
      }
    }
  };

  handleClickCallsList = () => {
    if (this.hasCallMouseDownFired) {
      this.hasCallMouseDownFired = false;
      return;
    }
    const { isCallsListVisible } = this.state;
    this.setState({ isCallsListVisible: !isCallsListVisible });
  };

  handleClickOpenCds = () => {
    const { isCdsVisible } = this.state;
    if (this.hasCdsMouseDownFired) {
      this.hasCdsMouseDownFired = false;
      return;
    }
    this.setState({
      isCdsVisible: !isCdsVisible,
    });
  };

  handleClickNotificationsList = () => {
    const { onMarkNotificationsAsRead } = this.props;
    const { isNotificationsListVisible, notificationData } = this.state;
    onMarkNotificationsAsRead();
    if (this.hasNotificationMouseDownFired) {
      this.hasNotificationMouseDownFired = false;
      return;
    }
    this.setState({
      isNotificationsListVisible: !isNotificationsListVisible,
      notificationData: notificationData.map(notification => ({ ...notification, status: 'read' })),
    });
  };

  handleClickAvatar = () => {
    if (this.hasProfileMouseDownFired) {
      this.hasProfileMouseDownFired = false;
      return;
    }
    const { isProfileDropdownVisible } = this.state;
    this.setState({ isProfileDropdownVisible: !isProfileDropdownVisible });
    this.setState({ isNotificationsListVisible: false });
  };

  handleClickProfileMenu = e => {
    const { uiStore } = this.props;
    if (e.key === 'logout') {
      return Router.push('/account/logout', '/account/logout');
    }
    if (e.key === 'bug') {
      return Router.push('/submit-issue', '/submit-issue');
    }
    if (e.key === 'switch-role') {
      return uiStore.openGlobalModal('switchRole');
    }
    return Router.push(`/${e.key}`, `/${e.key}`);
  };

  handleMouseDown = ev => {
    const {
      isAppDropdownVisible,
      isCallsListVisible,
      isCdsVisible,
      isProfileDropdownVisible,
      isNotificationsListVisible,
    } = this.state;

    if (
      isAppDropdownVisible &&
      this.appDropdown.current &&
      !this.appDropdown.current.contains(ev.target)
    ) {
      this.hasAppMouseDownFired = true;
      this.setState({ isAppDropdownVisible: false });
    } else {
      this.hasAppMouseDownFired = false;
    }

    if (
      isProfileDropdownVisible &&
      this.profileDropdown.current &&
      !this.profileDropdown.current.contains(ev.target)
    ) {
      this.hasProfileMouseDownFired = true;
      this.setState({ isProfileDropdownVisible: false });
    } else {
      this.hasProfileMouseDownFired = false;
    }

    if (
      isNotificationsListVisible &&
      this.notificationsListDropdown.current &&
      !this.notificationsListDropdown.current.contains(ev.target)
    ) {
      this.hasNotificationMouseDownFired = true;
      this.setState({ isNotificationsListVisible: false });
    } else {
      this.hasNotificationMouseDownFired = false;
    }

    let isCdsOptionClicked = false;
    const closestCdsOption = ev.target.closest('li') && ev.target.closest('li').firstElementChild;
    if (closestCdsOption) {
      const targetId = closestCdsOption.getAttribute('id');
      isCdsOptionClicked = targetId && targetId.includes('cds-option');
    }
    if (
      isCdsVisible &&
      this.cdsDropdown.current &&
      !(this.cdsDropdown.current.contains(ev.target) || isCdsOptionClicked)
    ) {
      this.hasCdsMouseDownFired = true;
      this.setState({ isCdsVisible: false });
    } else {
      this.hasCdsMouseDownFired = false;
    }

    let isCallsListItemClicked = false;
    const closestCallListItem =
      ev.target.closest('li') && ev.target.closest('li').firstElementChild;
    if (closestCallListItem) {
      const targetId = closestCallListItem.getAttribute('id');
      isCallsListItemClicked = targetId && targetId.includes('calls-list-item');
    }

    if (
      isCallsListVisible &&
      this.callsListDropdown.current &&
      !(this.callsListDropdown.current.contains(ev.target) || isCallsListItemClicked)
    ) {
      this.hasCallMouseDownFired = true;
      this.setState({ isCallsListVisible: false });
    } else {
      this.hasCallMouseDownFired = false;
    }
  };

  toggleAppMenu = () => {
    if (this.hasAppMouseDownFired) {
      this.hasAppMouseDownFired = false;
      return;
    }
    const { isAppDropdownVisible } = this.state;
    this.setState({ isAppDropdownVisible: !isAppDropdownVisible });
  };

  renderActionIcons() {
    const { authStore } = this.props;
    const { notificationData } = this.state;

    let profileAvatar = <Avatar icon="loading" size={40} />;
    if (authStore.myProfile?.image?.medium) {
      profileAvatar = <Avatar src={authStore.myProfile?.image.medium} size={40} />;
    }

    const canMakeCalls = authStore.myProfile?.permissions?.includes('makeCalls');
    const isProvider = authStore.role === 'provider';
    const counter = notificationData.filter(notification => notification.status === 'new').length;

    return (
      <>
        <div
          className="badgeWrapper"
          onClick={this.handleClickCallsList}
          tabIndex={0}
          role="button"
          onKeyDown={this.handleClickCallsList}
        >
          {canMakeCalls ? (
            <Badge count={counter}>
              <span className="head-example" />
              <Button shape="circle" icon="phone" size="large" ghost type="primary" />
            </Badge>
          ) : null}
        </div>
        <div
          className="badgeWrapper"
          onClick={this.handleClickOpenCds}
          tabIndex={0}
          role="button"
          onKeyDown={this.handleClickOpenCds}
        >
          {isProvider ? (
            <Button shape="circle" icon="file-search" size="large" ghost type="primary" />
          ) : null}
        </div>
        <div
          className="badgeWrapper"
          onClick={this.handleClickNotificationsList}
          tabIndex={0}
          role="button"
          onKeyDown={this.handleClickNotificationsList}
        >
          <Badge count={counter}>
            <span className="head-example" />
            <Button shape="circle" icon="bell" size="large" ghost type="primary" />
          </Badge>
        </div>
        <div
          className="avatar-container"
          onClick={this.handleClickAvatar}
          tabIndex={0}
          role="button"
          onKeyDown={this.handleClickAvatar}
        >
          {profileAvatar}
        </div>
      </>
    );
  }

  renderProfileDropdown() {
    const { authStore } = this.props;
    const { isProfileDropdownVisible } = this.state;

    if (!isProfileDropdownVisible) {
      return null;
    }

    let renderSwitchRole = null;
    if (authStore.myProfile?.roles.length > 1) {
      renderSwitchRole = (
        <Menu.Item key="switch-role">
          <div className="menu-item-content">
            <Icon type="swap" />
            <span>Switch role</span>
          </div>
        </Menu.Item>
      );
    }

    return (
      <DropdownWrapper ref={this.profileDropdown} right={16} cRight={25} width={180}>
        <div>
          <Menu mode="inline" onClick={this.handleClickProfileMenu}>
            <Menu.Item key="my-account">
              <div className="menu-item-content">
                <Icon type="user" />
                <span>My account</span>
              </div>
            </Menu.Item>
            {renderSwitchRole}
            <Menu.Item key="bug">
              <div className="menu-item-content">
                <Icon type="bug" />
                <span>Submit an issue</span>
              </div>
            </Menu.Item>
            <Menu.Item key="logout">
              <div className="menu-item-content">
                <Icon type="logout" />
                <span>Log out</span>
              </div>
            </Menu.Item>
          </Menu>
        </div>
      </DropdownWrapper>
    );
  }

  renderAppDropdown() {
    const { authStore } = this.props;
    const { isAppDropdownVisible } = this.state;

    const { role } = authStore;

    if (!isAppDropdownVisible) {
      return null;
    }

    const isSuperAdmin = checkIfSuperAdmin(authStore.myProfile?._id);

    const menusDisplay = menus.map(menu => {
      let shouldDisplay = false;
      if (menu.roles.includes(role)) {
        shouldDisplay = true;
        if (menu.displayRule && !isSuperAdmin) {
          shouldDisplay = menu.displayRule(authStore.myProfile, authStore.loginMethod);
        }
      }

      if (shouldDisplay) {
        return (
          <Menu.Item key={menu.key}>
            <div className="menu-item-content">
              <Icon type={menu.icon} />
              <span>{menu.name}</span>
            </div>
          </Menu.Item>
        );
      }
      return null;
    });

    return (
      <DropdownWrapper ref={this.appDropdown} left={40} width={240}>
        <div style={{ padding: '25px' }}>
          <Menu mode="inline" onClick={this.handleClickAppMenu}>
            <Menu.ItemGroup key="apps" title="Apps">
              {menusDisplay}
            </Menu.ItemGroup>
          </Menu>
        </div>
      </DropdownWrapper>
    );
  }

  renderCallsList() {
    const { isCallsListVisible, calls } = this.state;
    if (isCallsListVisible) {
      return (
        <div ref={this.callsListDropdown}>
          <CallsDropdown calls={calls} />
        </div>
      );
    }
    return null;
  }

  renderCdsDropdown() {
    const { isCdsVisible } = this.state;
    if (isCdsVisible) {
      return (
        <div ref={this.cdsDropdown}>
          <CdsDropdown />
        </div>
      );
    }
    return null;
  }

  renderNotificationsList() {
    const { isNotificationsListVisible, notificationData } = this.state;
    if (isNotificationsListVisible) {
      return (
        <div ref={this.notificationsListDropdown}>
          <NotificationsDropdown notifications={notificationData} />
        </div>
      );
    }
    return null;
  }

  render() {
    const { uiStore } = this.props;
    return (
      <>
        <Wrapper>
          <div
            className="menu-toggle"
            onClick={this.toggleAppMenu}
            tabIndex={0}
            role="button"
            onKeyDown={this.toggleAppMenu}
          >
            <Icon type="menu" />
          </div>
          <div className="top-container">
            <div className="top-container-2">
              <div className="logo-container">
                <Link href="/" as="/">
                  <img
                    src="https://5startelemed.nyc3.digitaloceanspaces.com/production/static/logo.png"
                    height="40px"
                    alt="logo"
                  />
                </Link>
              </div>
              <div className="icons-container">{this.renderActionIcons()}</div>
            </div>
          </div>
        </Wrapper>
        {this.renderCdsDropdown()}
        {this.renderCallsList()}
        {this.renderNotificationsList()}
        {this.renderAppDropdown()}
        {this.renderProfileDropdown()}
        <Modal
          {...uiStore.modalProps}
          visible={uiStore.isGlobalModalOpen}
          onCancel={uiStore.closeGlobalModal}
          destroyOnClose
          footer={null}
        >
          <GlobalModalContent />
        </Modal>
      </>
    );
  }
}

export default Topbar;
